import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X, Command } from 'lucide-react';
import { useDebounce } from '../../../hooks/use-debounce';
import { TypeaheadDropdown } from './typeahead-dropdown';
import { MobileSearchOverlay } from './mobile-search-overlay';
import { useTypeaheadSearch } from '../use-unified-search';

const SEARCH_HISTORY_KEY = 'candle_search_history';
const MAX_HISTORY_ITEMS = 3;

/**
 * HeaderSearch — the inline search bar for the desktop header
 * and search icon trigger for mobile.
 */
export function HeaderSearch() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [searchHistory, setSearchHistory] = useState<string[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Detect RTL languages
    const isRTL = ['ar', 'fa', 'ckb'].includes(i18n.language);

    const debouncedQuery = useDebounce(query, 300);
    const { data, isLoading } = useTypeaheadSearch(debouncedQuery, isFocused && debouncedQuery.length >= 3);

    const showDropdown = isFocused;

    // Load search history from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
            if (stored) {
                const history = JSON.parse(stored);
                setSearchHistory(Array.isArray(history) ? history : []);
            }
        } catch (error) {

        }
    }, []);

    // Save search to history
    const saveToHistory = useCallback((searchQuery: string) => {
        const trimmed = searchQuery.trim();
        if (trimmed.length < 3) return;

        try {
            setSearchHistory((prev) => {
                // Remove if already exists, then add to front
                const filtered = prev.filter(item => item !== trimmed);
                const updated = [trimmed, ...filtered].slice(0, MAX_HISTORY_ITEMS);
                localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updated));
                return updated;
            });
        } catch (error) {

        }
    }, []);

    // Keyboard shortcut: Ctrl+L / ⌘+L
    useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'l') {
                e.preventDefault();
                inputRef.current?.focus();
            }
        };
        document.addEventListener('keydown', handleGlobalKeyDown);
        return () => document.removeEventListener('keydown', handleGlobalKeyDown);
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsFocused(false);
                setSelectedIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reset selected index when query changes
    useEffect(() => {
        setSelectedIndex(-1);
    }, [query]);

    const handleSubmit = useCallback((searchQuery?: string) => {
        const q = (searchQuery || query).trim();
        if (q.length >= 3) {
            saveToHistory(q);
            navigate(`/search?q=${encodeURIComponent(q)}`);
            setIsFocused(false);
            setIsMobileOpen(false);
            setSelectedIndex(-1);
            inputRef.current?.blur();
        }
    }, [query, navigate, saveToHistory]);

    const handleResultSelect = useCallback((path: string) => {
        if (query.trim().length >= 3) {
            saveToHistory(query.trim());
        }
        setIsFocused(false);
        setSelectedIndex(-1);
        navigate(path);
    }, [query, navigate, saveToHistory]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        const showingHistory = query.length === 0 && searchHistory.length > 0;

        if (e.key === 'Enter') {
            if (showingHistory && selectedIndex >= 0) {
                // Select highlighted history item
                const historyItem = searchHistory[selectedIndex];
                if (historyItem) {
                    setQuery(historyItem);
                    handleSubmit(historyItem);
                }
            } else if (!showingHistory && selectedIndex >= 0) {
                // Navigate to selected search result
                const allResults = [
                    ...(data?.pages || []).map(p => ({ type: 'page' as const, id: p.slug || p.page_id })),
                    ...(data?.tags || []).map(t => ({ type: 'tag' as const, id: t.slug || t.tag_id, name: t.tag_name }))
                ];
                const selected = allResults[selectedIndex];
                if (selected) {
                    const path = selected.type === 'page'
                        ? `/memorial/${selected.id}`
                        : `/atag/${selected.id}`;
                    handleResultSelect(path);
                }
            } else {
                handleSubmit();
            }
        } else if (e.key === 'Escape') {
            setIsFocused(false);
            setSelectedIndex(-1);
            inputRef.current?.blur();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (showingHistory) {
                setSelectedIndex(prev => (prev + 1) % searchHistory.length);
            } else {
                const totalResults = (data?.pages?.length || 0) + (data?.tags?.length || 0);
                if (totalResults > 0) {
                    setSelectedIndex(prev => (prev + 1) % totalResults);
                }
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (showingHistory) {
                setSelectedIndex(prev => prev <= 0 ? searchHistory.length - 1 : prev - 1);
            } else {
                const totalResults = (data?.pages?.length || 0) + (data?.tags?.length || 0);
                if (totalResults > 0) {
                    setSelectedIndex(prev => prev <= 0 ? totalResults - 1 : prev - 1);
                }
            }
        }
    };

    const handleClear = () => {
        setQuery('');
        setSelectedIndex(-1);
        inputRef.current?.focus();
    };

    // Detect platform for shortcut hint
    const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

    return (
        <>
            {/* Desktop Search Bar */}
            <div ref={containerRef} className="header-search-container hidden md:block relative">
                <div
                    className={`header-search-bar ${isFocused ? 'header-search-bar--focused' : ''}`}
                >
                    <Search className="header-search-icon" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onFocus={() => setIsFocused(true)}
                        onKeyDown={handleKeyDown}
                        placeholder={t('unifiedSearch.placeholder')}
                        className={`header-search-input ${isRTL ? 'text-right' : ''}`}
                        dir={isRTL ? 'rtl' : 'ltr'}
                        autoComplete="off"
                        spellCheck={false}
                        id="header-search-input"
                    />
                    {query.length > 0 ? (
                        <button
                            onClick={handleClear}
                            className="header-search-clear"
                            aria-label={t('unifiedSearch.clear')}
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    ) : (
                        <kbd className="header-search-shortcut">
                            {isMac ? (
                                <>
                                    <Command className="h-3 w-3" />
                                    <span>L</span>
                                </>
                            ) : (
                                <span>Ctrl L</span>
                            )}
                        </kbd>
                    )}
                </div>

                {/* Typeahead dropdown */}
                {showDropdown && (
                    <TypeaheadDropdown
                        query={query}
                        debouncedQuery={debouncedQuery}
                        data={data}
                        isLoading={isLoading}
                        searchHistory={searchHistory}
                        selectedIndex={selectedIndex}
                        onClose={() => {
                            setIsFocused(false);
                            setSelectedIndex(-1);
                        }}
                        onViewAll={() => handleSubmit()}
                        onResultSelect={handleResultSelect}
                        onHistorySelect={(historyQuery) => {
                            setQuery(historyQuery);
                            handleSubmit(historyQuery);
                        }}
                    />
                )}
            </div>

            {/* Mobile Search Icon */}
            <button
                onClick={() => setIsMobileOpen(true)}
                className="md:hidden header-mobile-search-btn"
                aria-label={t('unifiedSearch.openSearch')}
            >
                <Search className="h-5 w-5" />
            </button>

            {/* Mobile Search Overlay */}
            {isMobileOpen && (
                <MobileSearchOverlay
                    onClose={() => setIsMobileOpen(false)}
                    onNavigate={(path: string) => {
                        setIsMobileOpen(false);
                        navigate(path);
                    }}
                />
            )}
        </>
    );
}
